// Inline-weight-edit + price-refresh state for the Holdings table. Tap
// "edit weights" → each WT cell becomes a numeric input; saving POSTs the
// new map to /api/portfolio and reloads so every dependent panel re-fetches.
//
// Add/remove are STRUCTURAL operations and auto-persist immediately, separate
// from the weights-edit save flow. commitAddPosition / commitRemovePosition
// build a full payload from current live weights + the structural change and
// POST → reload — so the new ticker appears across every panel (holdings,
// visualizer, sector pie, headline count) without a second click. This is the
// 2026-05-10 fix for "I added V then hit Update Portfolio and it disappeared":
// Update Portfolio reloads, which wipes any in-memory not-yet-saved adds.

import { useState } from 'react';
import { invalidateResource } from '@/lib/use-resource';

interface Holding {
  symbol: string;
  weight: number;
  price: number | null;
}

interface PortfolioShape {
  symbols: string[];
  shares?: Record<string, number>;
  bookNav?: number;
  entryPrices?: Record<string, number>;
}

const DEFAULT_BOOK_NAV = 1_000_000;

export interface UseHoldingsEditing {
  editing: boolean;
  draft: Record<string, string>;
  saving: boolean;
  refreshing: boolean;
  /** Symbols added during this edit session (not in original portfolio). */
  extraSymbols: string[];
  /** Symbols removed during this edit session (subset of original portfolio). */
  removedSymbols: Set<string>;
  /** Inline error from the most recent commitAdd/commitRemove. */
  commitError: string | null;
  setDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  startEdit(): void;
  cancelEdit(): void;
  refreshPrices(): Promise<void>;
  saveEdit(): Promise<void>;
  /** Append a ticker + initial weight%. No-op if already present (in original or extras). */
  addPosition(symbol: string, weightPct: number): { ok: boolean; error?: string };
  /** Mark a ticker for removal. If it was an extra, drop it from extras. */
  removePosition(symbol: string): void;
  /** Auto-persist: build full payload from current live weights + new position
   *  (shares × entry price → weight via book NAV). User-typed entry price is
   *  persisted as cost basis for tax-lot tracking. POST, reload. */
  commitAddPosition(symbol: string, shares: number, entryPrice: number): Promise<{ ok: boolean; error?: string }>;
  /** Auto-persist: build full payload minus this ticker, POST, reload. */
  commitRemovePosition(symbol: string): Promise<{ ok: boolean; error?: string }>;
  /** Auto-persist: build full payload reducing this ticker by N real shares.
   *  Cost basis preserved server-side (FIFO assumption — remaining shares
   *  retain their entry price). Sell price is informational. POST, reload. */
  commitReducePosition(symbol: string, sharesToSell: number, sellPrice?: number): Promise<{ ok: boolean; error?: string }>;
}

export function useHoldingsEditing(holdings: Holding[], portfolio: PortfolioShape | null | undefined): UseHoldingsEditing {
  const [editing, setEditing] = useState(false);
  // `draft` is the user's in-progress %-keyed map (string so the input
  // doesn't fight typing while the user enters multi-digit values).
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [extraSymbols, setExtraSymbols] = useState<string[]>([]);
  const [removedSymbols, setRemovedSymbols] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  function startEdit() {
    const seed: Record<string, string> = {};
    for (const h of holdings) seed[h.symbol] = (h.weight * 100).toFixed(1);
    setDraft(seed);
    setExtraSymbols([]);
    setRemovedSymbols(new Set());
    setEditing(true);
  }

  function cancelEdit() {
    setDraft({});
    setExtraSymbols([]);
    setRemovedSymbols(new Set());
    setEditing(false);
  }

  function addPosition(symbol: string, weightPct: number): { ok: boolean; error?: string } {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return { ok: false, error: 'enter a ticker' };
    if (!Number.isFinite(weightPct) || weightPct <= 0) return { ok: false, error: 'weight must be > 0' };
    const inOriginal = portfolio?.symbols.includes(sym) ?? false;
    if (inOriginal && !removedSymbols.has(sym)) {
      return { ok: false, error: `${sym} is already in the portfolio` };
    }
    if (extraSymbols.includes(sym)) {
      return { ok: false, error: `${sym} already added` };
    }
    // If the user is re-adding a symbol they just removed, restore it instead
    // of duplicating into extras.
    if (inOriginal && removedSymbols.has(sym)) {
      setRemovedSymbols(prev => {
        const next = new Set(prev);
        next.delete(sym);
        return next;
      });
      setDraft(d => ({ ...d, [sym]: weightPct.toFixed(1) }));
      return { ok: true };
    }
    setExtraSymbols(prev => [...prev, sym]);
    setDraft(d => ({ ...d, [sym]: weightPct.toFixed(1) }));
    return { ok: true };
  }

  function removePosition(symbol: string) {
    const sym = symbol.toUpperCase();
    // Drop any pending weight for this ticker.
    setDraft(d => {
      const next = { ...d };
      delete next[sym];
      return next;
    });
    if (extraSymbols.includes(sym)) {
      // Just-added ticker — un-add it.
      setExtraSymbols(prev => prev.filter(s => s !== sym));
      return;
    }
    // Original holding — soft-remove (will be excluded from saved symbols list).
    setRemovedSymbols(prev => {
      const next = new Set(prev);
      next.add(sym);
      return next;
    });
  }

  async function refreshPrices() {
    setRefreshing(true);
    try {
      await fetch('/api/refresh-prices', { method: 'POST' });
      // Bust the caches that depend on live prices, then reload so every
      // panel (holdings table, snapshot card, MTD/YTD totals) re-fetches.
      invalidateResource('/api/holdings-pnl');
      invalidateResource('/api/stocks');
      invalidateResource('/api/portfolio-snapshot');
      if (typeof window !== 'undefined') window.location.reload();
    } catch {
      setRefreshing(false);
    }
  }

  async function saveEdit() {
    if (!portfolio) return;
    // Final symbols list = (original \ removed) ∪ extras.
    const finalSymbols = [
      ...portfolio.symbols.filter(s => !removedSymbols.has(s)),
      ...extraSymbols,
    ];
    const weightsRaw: Record<string, number> = {};
    for (const sym of finalSymbols) {
      const n = parseFloat(draft[sym] ?? '');
      if (Number.isFinite(n) && n > 0) weightsRaw[sym] = n;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols: finalSymbols, weights: weightsRaw }),
      });
      const data = await res.json();
      if (data?.ok) {
        // Snapshot recomputed server-side already; reload so every dependent
        // panel re-fetches the fresh JSON. Drop cached GETs first so the
        // post-reload fetches don't return the pre-edit snapshot.
        invalidateResource('/api/portfolio');
        invalidateResource('/api/portfolio-snapshot');
        if (typeof window !== 'undefined') window.location.reload();
      }
    } finally {
      setSaving(false);
    }
  }

  // Auto-persist helpers. Build the full symbols + weights payload from the
  // CURRENT live holdings (not the in-edit draft) plus the structural change.
  // This is intentional: an "Add Position" or "Remove Position" click is a
  // structural mutation, not a weight edit. If the user had pending weight
  // edits in the draft they get discarded — they can re-edit after the reload.
  // (Acceptable tradeoff to avoid weird half-staged edits on auto-save.)
  async function postFullPayload(
    symbols: string[],
    weights: Record<string, number>,
    entryPrices?: Record<string, number>,
  ): Promise<{ ok: boolean; error?: string }> {
    setSaving(true);
    setCommitError(null);
    try {
      const body: { symbols: string[]; weights: Record<string, number>; entryPrices?: Record<string, number> } = { symbols, weights };
      if (entryPrices && Object.keys(entryPrices).length > 0) body.entryPrices = entryPrices;
      const res = await fetch('/api/portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data?.ok) {
        const msg = typeof data?.error === 'string' ? data.error : 'save failed';
        setCommitError(msg);
        return { ok: false, error: msg };
      }
      // Bust caches that depend on portfolio.json before reload so the post-
      // reload fetches don't return the pre-edit snapshot.
      invalidateResource('/api/portfolio');
      invalidateResource('/api/portfolio-snapshot');
      invalidateResource('/api/holdings-pnl');
      if (typeof window !== 'undefined') window.location.reload();
      return { ok: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setCommitError(msg);
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  }

  async function commitAddPosition(symbol: string, shares: number, entryPrice: number): Promise<{ ok: boolean; error?: string }> {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return { ok: false, error: 'enter a ticker' };
    if (!Number.isFinite(shares) || shares <= 0) return { ok: false, error: 'shares must be > 0' };
    if (!Number.isFinite(entryPrice) || entryPrice <= 0) return { ok: false, error: 'price must be > 0' };
    if (!portfolio) return { ok: false, error: 'portfolio not loaded' };
    if (portfolio.symbols.includes(sym)) return { ok: false, error: `${sym} already in the portfolio` };

    // Real shares × price = $ exposure → weight = $ exposure / book NAV.
    const nav = portfolio.bookNav ?? DEFAULT_BOOK_NAV;
    const weightPct = (shares * entryPrice / nav) * 100;
    if (weightPct <= 0) return { ok: false, error: 'resulting weight is 0' };
    if (weightPct > 100) return { ok: false, error: `position is ${weightPct.toFixed(1)}% of book — exceeds 100%` };

    // Build full payload from current live weights + new ticker.
    const symbols = [...portfolio.symbols, sym];
    const weights: Record<string, number> = {};
    for (const h of holdings) {
      // Live weight is a fraction (0–1); API expects %.
      weights[h.symbol] = h.weight * 100;
    }
    weights[sym] = weightPct;
    // Pass entryPrice through so the API stores the user-typed cost basis
    // rather than re-snapping to today's scoring price. Critical for tax.
    const entryPrices: Record<string, number> = { [sym]: entryPrice };
    return postFullPayload(symbols, weights, entryPrices);
  }

  async function commitRemovePosition(symbol: string): Promise<{ ok: boolean; error?: string }> {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return { ok: false, error: 'no ticker' };
    if (!portfolio) return { ok: false, error: 'portfolio not loaded' };
    if (!portfolio.symbols.includes(sym)) return { ok: false, error: `${sym} not in the portfolio` };
    if (portfolio.symbols.length <= 1) return { ok: false, error: 'cannot remove the last position' };

    const symbols = portfolio.symbols.filter(s => s !== sym);
    const weights: Record<string, number> = {};
    for (const h of holdings) {
      if (h.symbol === sym) continue;
      weights[h.symbol] = h.weight * 100;
    }
    return postFullPayload(symbols, weights);
  }

  async function commitReducePosition(symbol: string, sharesToSell: number, _sellPrice?: number): Promise<{ ok: boolean; error?: string }> {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return { ok: false, error: 'no ticker' };
    if (!Number.isFinite(sharesToSell) || sharesToSell <= 0) {
      return { ok: false, error: 'shares to sell must be > 0' };
    }
    if (!portfolio) return { ok: false, error: 'portfolio not loaded' };
    if (!portfolio.symbols.includes(sym)) return { ok: false, error: `${sym} not in the portfolio` };

    const target = holdings.find(h => h.symbol === sym);
    if (!target || target.weight <= 0) return { ok: false, error: `${sym} has no weight to trim` };

    const nav = portfolio.bookNav ?? DEFAULT_BOOK_NAV;
    const normalized = portfolio.shares?.[sym];
    if (typeof normalized !== 'number' || normalized <= 0) {
      return { ok: false, error: `${sym} has no stored shares` };
    }
    const realShares = normalized * nav;
    if (sharesToSell >= realShares) {
      return { ok: false, error: `you only hold ${realShares.toFixed(0)} shares of ${sym} — use Remove for full exit` };
    }

    // Trim the position by the requested share count. New weight is the
    // CURRENT live weight scaled by (1 − sharesToSell/realShares) — same as
    // saying "new $ exposure = current $ exposure × remaining-share fraction".
    const ratio = sharesToSell / realShares;
    const newPct = target.weight * 100 * (1 - ratio);
    if (newPct < 0.05) {
      return { ok: false, error: `${sharesToSell} shares leaves ${sym} under 0.05% — use Remove instead` };
    }

    const weights: Record<string, number> = {};
    for (const h of holdings) {
      weights[h.symbol] = h.symbol === sym ? newPct : h.weight * 100;
    }
    // _sellPrice is intentionally not persisted yet — realized P&L logging is
    // a future feature. Cost basis (entryPrices[sym]) is preserved server-side.
    return postFullPayload(portfolio.symbols, weights);
  }

  return {
    editing, draft, saving, refreshing,
    extraSymbols, removedSymbols, commitError,
    setDraft, startEdit, cancelEdit, refreshPrices, saveEdit,
    addPosition, removePosition,
    commitAddPosition, commitRemovePosition, commitReducePosition,
  };
}
